import { useEffect, useState } from 'react';
import {
  Plus,
  History as HistoryIcon,
  Upload as PromoteIcon,
  Ban as RetireIcon,
  RefreshCw as RefreshIcon,
  Eye as ViewIcon,
} from 'lucide-react';
import {
  Button,
  Chip,
  Input,
  Textarea,
  Switch,
  FormField,
  CoordioDropdown,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from '@coordio/ui';
import { CortexDialog, CortexDialogBody, CortexDialogFooter } from '../components/dialogs/CortexDialog';
import { guardrailService, getGuardrailErrorMessage } from '../services/guardrailService';
import {
  GuardrailAction,
  FailMode,
  LifecycleStatus,
  GuardrailSafetyTier,
  guardrailActionLabels,
  guardrailSafetyTierLabels,
  lifecycleStatusLabels,
  type GuardrailDto,
  type GuardrailSeverity,
  type PublishGuardrailVersionRequest,
} from '../types/guardrail';
import { useNotificationStore } from '../store/notificationStore';
import DataTable, { type DataTableColumn } from '../components/data-display/DataTable';
import { GuardrailsSectionNav } from '../components/guardrails/GuardrailsSectionNav';
import { GuardrailVersionDetail } from '../components/guardrails/GuardrailVersionDetail';
import { PageListLayout } from '../components/layouts/PageListLayout';

/** Detector type keys offered in the publish form (first-party detectors). */
const DETECTOR_TYPE_KEYS = ['pii', 'secrets', 'keyword', 'regex'] as const;

/** Sensible per-type starting ConfigJson, mirroring each detector's shipped defaults. */
const CONFIG_TEMPLATES: Record<string, string> = {
  pii: JSON.stringify(
    {
      detector: {
        entities: [
          { id: 'US_SSN', threshold: 0.8 },
          { id: 'CREDIT_CARD', threshold: 0.8 },
          { id: 'EMAIL', threshold: 0.8 },
          { id: 'PHONE', threshold: 0.8 },
        ],
        allowlist: [],
        masking: { strategy: 'PLACEHOLDER', placeholderFormat: '[{ENTITY}_REDACTED]' },
      },
    },
    null,
    2
  ),
  secrets: JSON.stringify(
    {
      detector: {
        detectors: ['PROVIDER_PATTERNS', 'PRIVATE_KEY_BLOCKS', 'JWT', 'CONNECTION_STRINGS', 'HIGH_ENTROPY'],
        entropy: { minLength: 20, minEntropyBits: 3.5 },
      },
    },
    null,
    2
  ),
  keyword: JSON.stringify(
    {
      detector: {
        terms: ['example-blocked-term'],
        matchWholeWord: true,
        caseSensitive: false,
      },
    },
    null,
    2
  ),
  regex: JSON.stringify(
    {
      detector: {
        patterns: [{ id: 'example-pattern', pattern: '\\bexample\\b', caseSensitive: false }],
      },
    },
    null,
    2
  ),
};

const statusColor = (status: number) => {
  switch (status) {
    case LifecycleStatus.Draft:
      return 'amber' as const;
    case LifecycleStatus.Active:
      return 'green' as const;
    case LifecycleStatus.Deprecated:
      return 'blue' as const;
    default:
      return 'default' as const;
  }
};

/** Violation actions offered in forms (subset of the full enum). */
const VIOLATION_ACTIONS = [
  GuardrailAction.Flag,
  GuardrailAction.Redact,
  GuardrailAction.Block,
  GuardrailAction.RequireApproval,
] as const;

/** Error actions offered in forms. */
const ERROR_ACTIONS = [GuardrailAction.Allow, GuardrailAction.Flag, GuardrailAction.Block] as const;

const SEVERITIES: GuardrailSeverity[] = ['Low', 'Medium', 'High', 'Critical'];

interface PublishFormState {
  guardrailKey: string;
  name: string;
  description: string;
  typeKey: string;
  configJson: string;
  defaultViolationAction: number;
  defaultInconclusiveAction: number;
  defaultErrorAction: number;
  defaultSeverity: GuardrailSeverity;
  defaultFailMode: number;
  defaultTimeoutMs: number;
  safetyTier: number;
  isNonSuppressible: boolean;
  minRecallTarget: string;
  owner: string;
  changeReason: string;
}

const emptyPublishForm = (): PublishFormState => ({
  guardrailKey: '',
  name: '',
  description: '',
  typeKey: 'pii',
  configJson: CONFIG_TEMPLATES.pii,
  defaultViolationAction: GuardrailAction.Flag,
  defaultInconclusiveAction: GuardrailAction.Flag,
  defaultErrorAction: GuardrailAction.Block,
  defaultSeverity: 'Medium',
  defaultFailMode: FailMode.FailClosed,
  defaultTimeoutMs: 250,
  safetyTier: GuardrailSafetyTier.Standard,
  isNonSuppressible: false,
  minRecallTarget: '',
  owner: '',
  changeReason: '',
});

/**
 * Guardrails registry page: current guardrail versions with publish / promote / retire actions.
 */
export const GuardrailsPage = () => {
  const { addNotification } = useNotificationStore();

  const [guardrails, setGuardrails] = useState<GuardrailDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [typeKeyFilter, setTypeKeyFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<number | ''>('');

  // Publish dialog
  const [publishOpen, setPublishOpen] = useState(false);
  const [publishForm, setPublishForm] = useState<PublishFormState>(emptyPublishForm());
  const [publishing, setPublishing] = useState(false);

  // Versions dialog
  const [versionsOpen, setVersionsOpen] = useState(false);
  const [versionsKey, setVersionsKey] = useState('');
  const [versions, setVersions] = useState<GuardrailDto[]>([]);
  const [versionsLoading, setVersionsLoading] = useState(false);

  // Promote dialog
  const [promoteTarget, setPromoteTarget] = useState<GuardrailDto | null>(null);
  const [promoteForm, setPromoteForm] = useState({
    approvedBy: '',
    approvedByRef: '',
    validationEvalRunId: '',
    changeReason: '',
  });
  const [promoting, setPromoting] = useState(false);

  // Retire dialog
  const [retireTarget, setRetireTarget] = useState<GuardrailDto | null>(null);
  const [retireReason, setRetireReason] = useState('');
  const [retiring, setRetiring] = useState(false);

  // View detail dialog
  const [detailTarget, setDetailTarget] = useState<GuardrailDto | null>(null);

  const loadGuardrails = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await guardrailService.getGuardrails({
        typeKey: typeKeyFilter || undefined,
        status: statusFilter === '' ? undefined : statusFilter,
      });
      setGuardrails(data);
    } catch (err: unknown) {
      const message = getGuardrailErrorMessage(err, 'Failed to load guardrails');
      setError(message);
      addNotification(message, 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadGuardrails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeKeyFilter, statusFilter]);

  const handleOpenPublish = () => {
    setPublishForm(emptyPublishForm());
    setPublishOpen(true);
  };

  const handleTypeKeyChange = (typeKey: string) => {
    setPublishForm((prev) => {
      const isUntouched = Object.values(CONFIG_TEMPLATES).includes(prev.configJson);
      return {
        ...prev,
        typeKey,
        configJson: isUntouched ? CONFIG_TEMPLATES[typeKey] ?? prev.configJson : prev.configJson,
      };
    });
  };

  const publishValid =
    publishForm.guardrailKey.trim() !== '' &&
    publishForm.name.trim() !== '' &&
    publishForm.owner.trim() !== '' &&
    publishForm.configJson.trim() !== '';

  const handlePublish = async () => {
    try {
      JSON.parse(publishForm.configJson);
    } catch {
      addNotification('Config JSON is not valid JSON', 'error');
      return;
    }

    const body: PublishGuardrailVersionRequest = {
      guardrailKey: publishForm.guardrailKey.trim(),
      name: publishForm.name.trim(),
      description: publishForm.description.trim() || undefined,
      typeKey: publishForm.typeKey,
      configJson: publishForm.configJson,
      defaultViolationAction: publishForm.defaultViolationAction,
      defaultInconclusiveAction: publishForm.defaultInconclusiveAction,
      defaultErrorAction: publishForm.defaultErrorAction,
      defaultSeverity: publishForm.defaultSeverity,
      defaultFailMode: publishForm.defaultFailMode,
      defaultTimeoutMs: publishForm.defaultTimeoutMs,
      safetyTier: publishForm.safetyTier,
      isNonSuppressible: publishForm.isNonSuppressible,
      minRecallTarget: publishForm.minRecallTarget ? Number(publishForm.minRecallTarget) : undefined,
      owner: publishForm.owner.trim(),
      changeReason: publishForm.changeReason.trim() || undefined,
    };

    setPublishing(true);
    try {
      await guardrailService.publishVersion(body);
      addNotification('Guardrail version published as Draft', 'success');
      setPublishOpen(false);
      loadGuardrails();
    } catch (err: unknown) {
      addNotification(getGuardrailErrorMessage(err, 'Failed to publish guardrail version'), 'error');
    } finally {
      setPublishing(false);
    }
  };

  const handleViewVersions = async (guardrailKey: string) => {
    setVersionsKey(guardrailKey);
    setVersionsOpen(true);
    setVersionsLoading(true);
    try {
      const data = await guardrailService.getVersions(guardrailKey);
      setVersions(data);
    } catch (err: unknown) {
      addNotification(getGuardrailErrorMessage(err, 'Failed to load versions'), 'error');
      setVersions([]);
    } finally {
      setVersionsLoading(false);
    }
  };

  const handleOpenPromote = (row: GuardrailDto) => {
    setPromoteForm({
      approvedBy: '',
      approvedByRef: '',
      validationEvalRunId: crypto.randomUUID(),
      changeReason: '',
    });
    setPromoteTarget(row);
  };

  const handlePromote = async () => {
    if (!promoteTarget) return;
    setPromoting(true);
    try {
      await guardrailService.promoteVersion(promoteTarget.guardrailKey, promoteTarget.version, {
        approvedBy: promoteForm.approvedBy.trim(),
        approvedByRef: promoteForm.approvedByRef.trim() || undefined,
        validationEvalRunId: promoteForm.validationEvalRunId.trim(),
        changeReason: promoteForm.changeReason.trim() || undefined,
      });
      addNotification('Guardrail version promoted to Active', 'success');
      setPromoteTarget(null);
      loadGuardrails();
      if (versionsOpen && versionsKey) handleViewVersions(versionsKey);
    } catch (err: unknown) {
      addNotification(getGuardrailErrorMessage(err, 'Failed to promote guardrail version'), 'error');
    } finally {
      setPromoting(false);
    }
  };

  const handleRetire = async () => {
    if (!retireTarget || !retireReason.trim()) return;
    setRetiring(true);
    try {
      await guardrailService.retireVersion(retireTarget.guardrailKey, retireTarget.version, {
        changeReason: retireReason.trim(),
      });
      addNotification('Guardrail version retired', 'success');
      setRetireTarget(null);
      setRetireReason('');
      loadGuardrails();
      if (versionsOpen && versionsKey) handleViewVersions(versionsKey);
    } catch (err: unknown) {
      addNotification(getGuardrailErrorMessage(err, 'Failed to retire guardrail version'), 'error');
    } finally {
      setRetiring(false);
    }
  };

  const viewDetailAction = (row: GuardrailDto) => (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button variant="ghost" size="icon" onClick={() => setDetailTarget(row)}>
            <ViewIcon className="h-4 w-4" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>View detail</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );

  const rowActions = (row: GuardrailDto) => (
    <div className="flex gap-0.5 justify-end">
      {viewDetailAction(row)}
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="ghost" size="icon" onClick={() => handleViewVersions(row.guardrailKey)}>
              <HistoryIcon className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>View versions</TooltipContent>
        </Tooltip>
      </TooltipProvider>
      {row.status === LifecycleStatus.Draft && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="ghost" size="icon" className="bg-green-600 hover:bg-green-700 text-white" onClick={() => handleOpenPromote(row)}>
                <PromoteIcon className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Promote to Active</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
      {row.status !== LifecycleStatus.Retired && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="destructive" size="icon" onClick={() => setRetireTarget(row)}>
                <RetireIcon className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Retire</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </div>
  );

  const versionHistoryActions = (row: GuardrailDto) => (
    <div className="flex gap-0.5 justify-end">
      {viewDetailAction(row)}
      {row.status === LifecycleStatus.Draft && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="ghost" size="icon" className="bg-green-600 hover:bg-green-700 text-white" onClick={() => handleOpenPromote(row)}>
                <PromoteIcon className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Promote to Active</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
      {row.status !== LifecycleStatus.Retired && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="destructive" size="icon" onClick={() => setRetireTarget(row)}>
                <RetireIcon className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Retire</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </div>
  );

  const tableColumns: DataTableColumn<GuardrailDto>[] = [
    {
      id: 'guardrailKey',
      label: 'Key',
      minWidth: 160,
      render: (row) => (
        <span className="text-sm font-mono text-[0.8125rem]">
          {row.guardrailKey}
        </span>
      ),
    },
    {
      id: 'name',
      label: 'Name',
      minWidth: 180,
      render: (row) => (
        <div>
          <span className="text-sm font-medium">
            {row.name}
          </span>
          {row.description && (
            <span className="block text-xs text-[var(--color-text-secondary)]">
              {row.description}
            </span>
          )}
        </div>
      ),
    },
    {
      id: 'typeKey',
      label: 'Type',
      minWidth: 100,
      render: (row) => <Chip label={row.typeKey} variant="outlined" className="text-xs" />,
    },
    { id: 'version', label: 'Version', minWidth: 80, render: (row) => `v${row.version}` },
    {
      id: 'status',
      label: 'Status',
      minWidth: 100,
      render: (row) => (
        <Chip
          label={lifecycleStatusLabels[row.status] ?? row.status}
          color={statusColor(row.status)}
          className="text-xs"
        />
      ),
    },
    {
      id: 'safetyTier',
      label: 'Safety Tier',
      minWidth: 100,
      render: (row) => guardrailSafetyTierLabels[row.safetyTier] ?? row.safetyTier,
    },
    {
      id: 'defaultViolationAction',
      label: 'Default Action',
      minWidth: 130,
      render: (row) => guardrailActionLabels[row.defaultViolationAction] ?? row.defaultViolationAction,
    },
    { id: 'owner', label: 'Owner', minWidth: 120 },
    {
      id: 'updatedAt',
      label: 'Updated',
      minWidth: 160,
      render: (row) => new Date(row.updatedAt).toLocaleString(),
    },
    { id: 'actions', label: 'Actions', minWidth: 130, align: 'right', render: rowActions },
  ];

  const versionColumns: DataTableColumn<GuardrailDto>[] = [
    { id: 'version', label: 'Version', minWidth: 70, render: (row) => `v${row.version}` },
    {
      id: 'status',
      label: 'Status',
      minWidth: 100,
      render: (row) => (
        <Chip
          label={lifecycleStatusLabels[row.status] ?? row.status}
          color={statusColor(row.status)}
          className="text-xs"
        />
      ),
    },
    {
      id: 'isCurrent',
      label: 'Current',
      minWidth: 80,
      render: (row) => (row.isCurrent ? <Chip label="Current" color="violet" className="text-xs" /> : '-'),
    },
    { id: 'approvedBy', label: 'Approved By', minWidth: 120, render: (row) => row.approvedBy || '-' },
    { id: 'changeReason', label: 'Change Reason', minWidth: 160, render: (row) => row.changeReason || '-' },
    {
      id: 'createdAt',
      label: 'Created',
      minWidth: 150,
      render: (row) => new Date(row.createdAt).toLocaleString(),
    },
    { id: 'actions', label: 'Actions', minWidth: 110, align: 'right', render: versionHistoryActions },
  ];

  return (
    <>
      <PageListLayout
        title="Guardrails"
        subtitle="Versioned detector registry: publish drafts, promote with an approval stamp, retire."
        createButton={{
          label: 'Publish Version',
          onClick: handleOpenPublish,
          icon: <Plus />,
        }}
        headerActions={
          <Button variant="outline" onClick={loadGuardrails} disabled={loading} className="gap-2">
            <RefreshIcon className="h-4 w-4" />
            Refresh
          </Button>
        }
        filters={
          <div className="flex gap-4 items-start">
            <div className="w-[255px]">
              <CoordioDropdown
                label="Type"
                options={[
                  { value: '', label: 'All' },
                  ...DETECTOR_TYPE_KEYS.map((key) => ({ value: key, label: key })),
                ]}
                value={typeKeyFilter}
                onChange={(val) => setTypeKeyFilter(val ?? '')}
              />
            </div>
            <div className="w-[255px]">
              <CoordioDropdown
                label="Status"
                options={[
                  { value: '', label: 'All' },
                  ...Object.entries(lifecycleStatusLabels).map(([value, label]) => ({
                    value,
                    label,
                  })),
                ]}
                value={statusFilter === '' ? '' : String(statusFilter)}
                onChange={(val) => {
                  setStatusFilter(!val || val === '' ? '' : Number(val));
                }}
              />
            </div>
          </div>
        }
        loading={loading}
        error={error || undefined}
        onErrorDismiss={() => setError(null)}
        isEmpty={!loading && guardrails.length === 0}
        emptyState={{
          title: 'No guardrails found',
          description: 'Publish a new guardrail version to get started.',
          action: { label: 'Publish Version', onClick: handleOpenPublish },
        }}
      >
        <GuardrailsSectionNav />
        <DataTable columns={tableColumns} rows={guardrails} />
      </PageListLayout>

      {/* Publish Version Dialog */}
      <CortexDialog open={publishOpen} onClose={() => setPublishOpen(false)} size="lg" title="Publish Guardrail Version">
        <CortexDialogBody>
          <div className="flex flex-col gap-5 pt-1">
            <div className="grid gap-4 items-end sm:grid-cols-2">
              <FormField label="Guardrail Key" required description="Stable identity across versions, e.g. pii-basic">
                <Input
                  value={publishForm.guardrailKey}
                  onChange={(e) => setPublishForm({ ...publishForm, guardrailKey: e.target.value })}
                />
              </FormField>
              <FormField label="Name" required>
                <Input
                  value={publishForm.name}
                  onChange={(e) => setPublishForm({ ...publishForm, name: e.target.value })}
                />
              </FormField>
            </div>
            <FormField label="Description">
              <Input
                value={publishForm.description}
                onChange={(e) => setPublishForm({ ...publishForm, description: e.target.value })}
              />
            </FormField>
            <div className="max-w-[280px]">
              <CoordioDropdown
                label="Detector Type"
                options={DETECTOR_TYPE_KEYS.map((key) => ({ value: key, label: key }))}
                value={publishForm.typeKey}
                onChange={(val) => handleTypeKeyChange(val ?? 'pii')}
              />
            </div>
            <FormField label="Config JSON" required>
              <Textarea
                className="font-mono text-[0.8125rem]"
                rows={8}
                value={publishForm.configJson}
                onChange={(e) => setPublishForm({ ...publishForm, configJson: e.target.value })}
              />
            </FormField>

            <div className="flex items-center gap-3">
              <hr className="flex-1 border-t border-[var(--color-border-default)]" />
              <span className="text-xs text-[var(--color-text-secondary)]">Defaults</span>
              <hr className="flex-1 border-t border-[var(--color-border-default)]" />
            </div>

            <div className="grid gap-4 items-end sm:grid-cols-3">
              <CoordioDropdown
                label="Violation Action"
                options={VIOLATION_ACTIONS.map((action) => ({
                  value: String(action),
                  label: guardrailActionLabels[action],
                }))}
                value={String(publishForm.defaultViolationAction)}
                onChange={(val) =>
                  setPublishForm({ ...publishForm, defaultViolationAction: Number(val) })
                }
              />
              <CoordioDropdown
                label="Inconclusive Action"
                options={ERROR_ACTIONS.map((action) => ({
                  value: String(action),
                  label: guardrailActionLabels[action],
                }))}
                value={String(publishForm.defaultInconclusiveAction)}
                onChange={(val) =>
                  setPublishForm({ ...publishForm, defaultInconclusiveAction: Number(val) })
                }
              />
              <CoordioDropdown
                label="Error Action"
                options={ERROR_ACTIONS.map((action) => ({
                  value: String(action),
                  label: guardrailActionLabels[action],
                }))}
                value={String(publishForm.defaultErrorAction)}
                onChange={(val) =>
                  setPublishForm({ ...publishForm, defaultErrorAction: Number(val) })
                }
              />
            </div>
            <div className="grid gap-4 items-end sm:grid-cols-3">
              <CoordioDropdown
                label="Severity"
                options={SEVERITIES.map((sev) => ({ value: sev, label: sev }))}
                value={publishForm.defaultSeverity}
                onChange={(val) =>
                  setPublishForm({ ...publishForm, defaultSeverity: val as GuardrailSeverity })
                }
              />
              <CoordioDropdown
                label="Fail Mode"
                options={[
                  { value: String(FailMode.FailOpen), label: 'Fail open' },
                  { value: String(FailMode.FailClosed), label: 'Fail closed' },
                ]}
                value={String(publishForm.defaultFailMode)}
                onChange={(val) => setPublishForm({ ...publishForm, defaultFailMode: Number(val) })}
              />
              <CoordioDropdown
                label="Safety Tier"
                options={Object.entries(guardrailSafetyTierLabels).map(([value, label]) => ({
                  value,
                  label,
                }))}
                value={String(publishForm.safetyTier)}
                onChange={(val) => setPublishForm({ ...publishForm, safetyTier: Number(val) })}
              />
            </div>
            <div className="grid gap-4 items-end sm:grid-cols-2">
              <FormField label="Timeout (ms)">
                <Input
                  type="number"
                  value={publishForm.defaultTimeoutMs}
                  onChange={(e) =>
                    setPublishForm({ ...publishForm, defaultTimeoutMs: Number(e.target.value) || 0 })
                  }
                />
              </FormField>
              <FormField label="Min Recall Target" description="Optional, 0–1; Tier 0 requires ≥ 0.98">
                <Input
                  type="number"
                  value={publishForm.minRecallTarget}
                  onChange={(e) => setPublishForm({ ...publishForm, minRecallTarget: e.target.value })}
                />
              </FormField>
            </div>
            <div className="ui-form-choice-field">
              <div className="ui-form-choice-field__row">
                <div className="ui-form-choice-field__control">
                  <Switch
                    checked={publishForm.isNonSuppressible}
                    onCheckedChange={(checked) => setPublishForm({ ...publishForm, isNonSuppressible: checked })}
                  />
                </div>
                <div className="ui-form-choice-field__label-cell">
                  <span className="ui-form-choice-field__label">Non-suppressible</span>
                </div>
              </div>
            </div>
            <div className="grid gap-4 items-end sm:grid-cols-2">
              <FormField label="Owner" required>
                <Input
                  value={publishForm.owner}
                  onChange={(e) => setPublishForm({ ...publishForm, owner: e.target.value })}
                />
              </FormField>
              <FormField label="Change Reason">
                <Input
                  value={publishForm.changeReason}
                  onChange={(e) => setPublishForm({ ...publishForm, changeReason: e.target.value })}
                />
              </FormField>
            </div>
          </div>
        </CortexDialogBody>
        <CortexDialogFooter>
          <Button variant="outline" onClick={() => setPublishOpen(false)}>Cancel</Button>
          <Button onClick={handlePublish} disabled={!publishValid || publishing}>
            Publish Draft
          </Button>
        </CortexDialogFooter>
      </CortexDialog>

      {/* Versions Dialog */}
      <CortexDialog open={versionsOpen} onClose={() => setVersionsOpen(false)} size="lg" title={<>Versions of <code>{versionsKey}</code></>}>
        <CortexDialogBody>
          <DataTable
            columns={versionColumns}
            rows={versions}
            loading={versionsLoading}
            emptyMessage="No versions found"
            size="small"
          />
        </CortexDialogBody>
        <CortexDialogFooter>
          <Button variant="outline" onClick={() => setVersionsOpen(false)}>Close</Button>
        </CortexDialogFooter>
      </CortexDialog>

      {/* Promote Dialog */}
      <CortexDialog open={!!promoteTarget} onClose={() => setPromoteTarget(null)} size="md" title={`Promote ${promoteTarget?.guardrailKey} v${promoteTarget?.version} to Active`}>
        <CortexDialogBody>
          <div className="flex flex-col gap-6 pt-1">
            <FormField label="Approved By" required>
              <Input
                value={promoteForm.approvedBy}
                onChange={(e) => setPromoteForm({ ...promoteForm, approvedBy: e.target.value })}
              />
            </FormField>
            <FormField label="Approver Reference (optional)" description="NPI / signature / change ticket">
              <Input
                value={promoteForm.approvedByRef}
                onChange={(e) => setPromoteForm({ ...promoteForm, approvedByRef: e.target.value })}
              />
            </FormField>
            <FormField label="Validation Eval Run ID" required description="Pre-filled with a random GUID for test convenience">
              <Input
                value={promoteForm.validationEvalRunId}
                onChange={(e) => setPromoteForm({ ...promoteForm, validationEvalRunId: e.target.value })}
              />
            </FormField>
            <FormField label="Change Reason">
              <Input
                value={promoteForm.changeReason}
                onChange={(e) => setPromoteForm({ ...promoteForm, changeReason: e.target.value })}
              />
            </FormField>
          </div>
        </CortexDialogBody>
        <CortexDialogFooter>
          <Button variant="outline" onClick={() => setPromoteTarget(null)}>Cancel</Button>
          <Button
            className="bg-green-600 hover:bg-green-700 text-white"
            onClick={handlePromote}
            disabled={promoting || !promoteForm.approvedBy.trim() || !promoteForm.validationEvalRunId.trim()}
          >
            Promote
          </Button>
        </CortexDialogFooter>
      </CortexDialog>

      {/* View Detail Dialog */}
      <CortexDialog open={!!detailTarget} onClose={() => setDetailTarget(null)} size="lg" title={`${detailTarget?.guardrailKey} v${detailTarget?.version}`}>
        <CortexDialogBody>
          {detailTarget && <GuardrailVersionDetail guardrail={detailTarget} />}
        </CortexDialogBody>
        <CortexDialogFooter>
          <Button variant="outline" onClick={() => setDetailTarget(null)}>Close</Button>
        </CortexDialogFooter>
      </CortexDialog>

      {/* Retire Dialog */}
      <CortexDialog open={!!retireTarget} onClose={() => setRetireTarget(null)} size="md" title={`Retire ${retireTarget?.guardrailKey} v${retireTarget?.version}`}>
        <CortexDialogBody>
          <div className="pt-1">
            <FormField label="Change Reason" required>
              <Textarea
                rows={3}
                value={retireReason}
                onChange={(e) => setRetireReason(e.target.value)}
              />
            </FormField>
          </div>
        </CortexDialogBody>
        <CortexDialogFooter>
          <Button variant="outline" onClick={() => setRetireTarget(null)}>Cancel</Button>
          <Button
            variant="destructive"
            onClick={handleRetire}
            disabled={retiring || !retireReason.trim()}
          >
            Retire
          </Button>
        </CortexDialogFooter>
      </CortexDialog>
    </>
  );
};
